import { ProgressJoinDatasetsTool, RenderJoinDatasetsTool } from './JoinDatasetsTool.render.tsx';
import { type JoinDatasetsInput, JoinDatasetsInputSchema, type JoinDatasetsOutput } from './schema.ts';
import { formatStatsSection } from '#plugin-chat/components/tools/helpers/format-tool-message.ts';
import { computeAutoStats } from '#plugin-chat/lib/data-pipeline/engine/auto-stats.ts';
import { joinDatasets } from '#plugin-chat/lib/data-pipeline/engine/join.ts';
import type { Dataset } from '#plugin-chat/lib/data-pipeline/types.ts';
import { ToolGuidance } from '#plugin-chat/lib/instructions/components.tsx';
import { createTool } from '#plugin-chat/lib/tool/create-tool.tsx';
import type { ProducerToolConfig } from '#plugin-chat/lib/tool/create-tool.types.ts';

/**
 * Tool for joining two stored datasets on a shared key column.
 *
 * Resolves both inputs via `ctx.resolve.byName`, delegates to the engine's
 * {@link joinDatasets} helper, and stages the combined dataset under
 * `args.outputDataset`. For any subsequent filter / derive / sort / rollup
 * work, the LLM chains a follow-up `transformDataset` call on the joined
 * output.
 *
 * Producer tool: the framework calls `produceDataset` first, stages the
 * result under `args.outputDataset`, and passes it into `execute`. Recovery
 * post-reload re-runs `produceDataset`, cascading into upstream recovery
 * for both `leftDataset` and `rightDataset` via `ctx.resolve.byName(...)`.
 */
export const JoinDatasetsTool = createTool<object, JoinDatasetsInput, JoinDatasetsOutput>('joinDatasets', () => ({
  instructions: (
    <ToolGuidance
      tool={JoinDatasetsTool}
      whenNotToUse={[
        'Filtering, deriving, sorting, aggregating, or reshaping a dataset — use transformDataset',
        'Storing literal/computed data — use registerDataset',
      ]}
      whenToUse={[
        'Combining two stored datasets on a shared key column before further processing',
        'Enriching a primary dataset with attributes from a lookup table',
      ]}
    >
      <h3>Join semantics</h3>
      <ul>
        <li>
          <code>how: "left"</code> (default) keeps all left rows, adding nulls for non-matches.
        </li>
        <li>
          <code>how: "inner"</code> keeps only rows that match in both datasets.
        </li>
        <li>
          <code>how: "right"</code> / <code>"full"</code> mirror SQL semantics.
        </li>
        <li>
          Conflicting non-key column names from the right dataset are auto-suffixed with <code>_right</code>.
        </li>
        <li>
          Join keys accept dot-notation paths into nested object columns (e.g. <code>well.externalId</code>) on either
          side, matching the semantics of filter.
        </li>
      </ul>
      <p>
        For any downstream transformation on the joined result (filter, derive, sort, rollup, reshape), chain a
        follow-up <code>transformDataset</code> call with <code>inputDataset</code> set to this tool's
        <code>outputDataset</code>.
      </p>
    </ToolGuidance>
  ),
  config: {
    parameters: JoinDatasetsInputSchema,
    render: RenderJoinDatasetsTool,
    progress: ProgressJoinDatasetsTool,
    description: <p>Join two stored datasets on a shared key column.</p>,
    produceDataset: async (args, ctx): Promise<Dataset> => {
      const left = await ctx.resolve.byName(args.leftDataset);
      const right = await ctx.resolve.byName(args.rightDataset);

      // joinDatasets throws ColumnNotFoundError on join-key resolution failure;
      // the tool boundary in createTool catches and forwards the structured response.
      const result = joinDatasets(left, right, { on: args.on, how: args.how });
      return result.dataset;
    },
    execute: ({ args, dataset }) => {
      const rows = dataset.rows as Record<string, unknown>[];
      const combined = rows.length;
      const { joinStats } = dataset.meta ?? {};
      const leftN = joinStats?.leftRowCount ?? 0;
      const rightN = joinStats?.rightRowCount ?? 0;

      // Zero-row early return: skip stats work and emit a minimal envelope. Quotes
      // both input sizes so the LLM can distinguish "inner join produced no
      // matches" from "an input was empty".
      if (combined === 0) {
        return {
          success: true as const,
          outputDataset: args.outputDataset,
          message: `Join "${args.leftDataset}" (${leftN} rows) ⋈ "${args.rightDataset}" (${rightN} rows) produced 0 rows (${args.how} join).`,
        };
      }

      const columnNames = dataset.columns.map((c) => c.name);
      const autoStats = computeAutoStats(rows);

      const message = `Joined "${args.leftDataset}" (${leftN} rows) and "${args.rightDataset}" (${rightN} rows) into "${args.outputDataset}" (${combined} rows, ${args.how} join).

COLUMNS (${columnNames.length}): ${columnNames.join(', ')}

COLUMN STATS:
${formatStatsSection(autoStats)}`;

      return {
        success: true as const,
        outputDataset: args.outputDataset,
        message,
      };
    },
  } satisfies ProducerToolConfig<JoinDatasetsInput, JoinDatasetsOutput>,
}));
